// ArbiterWorkbench — per-agent wrapper over <CompositionWorkbench> for
// ARBITER, the Consumer-Duty fair-value-assessment composer. Surfaces the
// deterministically-computed fair-value verdict pill via `renderSections`.

import CompositionWorkbench from '.';
import { compositionAgentConfig } from './composition-agent-config';
import { cn } from '@/lib/utils';
import type { BaseDraft } from '@/data/types';

export interface ArbiterDraft extends BaseDraft {
    verdict?: 'fair_value' | 'not_fair_value' | 'review_required';
}

interface ArbiterWorkbenchProps {
    draft: ArbiterDraft;
    readOnly?: boolean;
    onApproved?: (draft: ArbiterDraft) => void;
    onRejected?: (draft: ArbiterDraft) => void;
    onRevisionRequested?: (draft: ArbiterDraft) => void;
}

const VERDICT_META: Record<string, { label: string; cls: string }> = {
    fair_value: {
        label: 'Fair value',
        cls: 'bg-[rgb(var(--color-rag-green)/0.15)] text-[rgb(var(--color-rag-green))]',
    },
    not_fair_value: {
        label: 'Not fair value',
        cls: 'bg-[rgb(var(--color-rag-red)/0.15)] text-[rgb(var(--color-rag-red))]',
    },
    review_required: {
        label: 'Review required',
        cls: 'bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))]',
    },
};

export default function ArbiterWorkbench({
    draft,
    readOnly,
    onApproved,
    onRejected,
    onRevisionRequested,
}: ArbiterWorkbenchProps) {
    return (
        <CompositionWorkbench<ArbiterDraft>
            agentName="ARBITER"
            draft={draft}
            table={compositionAgentConfig.ARBITER.table}
            actions={compositionAgentConfig.ARBITER.actions}
            readOnly={readOnly}
            renderSections={(d) => {
                if (!d.verdict) return null;
                const meta = VERDICT_META[d.verdict];
                return (
                    <div data-testid="arbiter-verdict" className="flex items-center gap-2">
                        <span className="text-annotation text-[rgb(var(--color-text-secondary))]">
                            Fair-value verdict
                        </span>
                        <span
                            className={cn(
                                'text-annotation px-2 py-0.5 rounded-button font-medium',
                                meta.cls,
                            )}
                        >
                            {meta.label}
                        </span>
                    </div>
                );
            }}
            onApproved={onApproved}
            onRejected={onRejected}
            onRevisionRequested={onRevisionRequested}
        />
    );
}
